import { ContactData } from '../../dto/EntityRepository';
import { ContactResponse } from '../../dto/ResDtoRepository';
import { UpdateContactReq } from '../../dto/ReqDtoRepository';
import { getContact, getContactForOwner, updateContact } from '../../api/contactApi';
import { useContactStore } from '../../store/contactStore';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { useEditModeStore } from '../../store/authStore';


interface UseContactStateResult {
  contact: ContactData | null;
  contactOwnerAui: string | null;
  contactScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseContactActionsResult {
  getContact: () => Promise<void>;
  getContactOwner: () => Promise<void>;
  updateContact: (data: UpdateContactReq) => Promise<void>;
}

export const useContactState = (): UseContactStateResult => {
  const contact = useContactStore((state) => state.contact);
  const contactOwnerAui = useContactStore((state) => state.contactOwnerAui);
  const contactScope = useContactStore((state) => state.contactScope);
  const status = useContactStore((state) => state.status);

  return { contact, contactOwnerAui, contactScope, status };
};

export const useContactActions = (): UseContactActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((state) => state.pageOwner?.aui);
  const beginContactFetch = useContactStore((state) => state.beginContactFetch);
  const setContact = useContactStore((state) => state.setContact);
  const setContactError = useContactStore((state) => state.setContactError);
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const withApiHandler = useApiWrapper();

  const handleContactSuccess = (
    response: ContactResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    const contactData = response.data;
    setContact(contactData, ownerAui, scope);
  };

  const handleUpdateContactSuccess = (
    response: ContactResponse,
    ownerAui: string,
  ) => {
    const contactData = response.data;
    setContact(contactData, ownerAui, 'owner');
    useEditModeStore.getState().setIsEditMode(false);
    setUpdatedTempAlert();
  };

  const fetchContact = async (aui: string, scope: ResourceScope) => {
    beginContactFetch(aui, scope);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => (scope === 'owner' ? getContactForOwner(aui) : getContact(aui)),
        (response) => handleContactSuccess(response, aui, scope),
      );
    } catch (error) {
      setContactError();
      throw error;
    }
  };

  const handleContactRequest = async (
    action: 'get' | 'get_for_owner' | 'update',
    data?: UpdateContactReq
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'update':
          if (!data) throw new Error('Data is required for update');
          const updateData = data satisfies UpdateContactReq;
          return runCurrentArchiveRequest(
            aui,
            () => updateContact(aui, updateData),
            (response) => handleUpdateContactSuccess(response, aui),
          );
        case 'get_for_owner':
          return fetchContact(aui, 'owner');
        case 'get':
        default:
          return fetchContact(aui, 'public');
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getContactHandler = () => handleContactRequest('get');
  const getContactOwnerHandler = () => handleContactRequest('get_for_owner');
  const updateContactHandler = (data: UpdateContactReq) => handleContactRequest('update', data);

  return {
    getContact: getContactHandler,
    getContactOwner: getContactOwnerHandler,
    updateContact: updateContactHandler,
  };
};
